/**
 * Point Cloud Bidirectional Mappings
 * 
 * Builds efficient lookup structures for spatial queries:
 * - cameraToPoints: Map<cameraId, Set<pointId>> - Which points does each camera observe?
 * - pointToCameras: Map<pointId, Set<cameraId>> - Which cameras observe each point?
 * 
 * These mappings enable O(1) lookups for:
 * - Finding all 3D points visible from a camera (for ray casting)
 * - Finding all cameras that observe a specific point (for pinch zoom, tap-to-focus)
 */

import { Camera, Point3D } from '@/types/colmap'

export interface PointCloudMappings {
  cameraToPoints: Map<number, Set<number>>
  pointToCameras: Map<number, Set<number>>
}

/**
 * Build bidirectional mappings between cameras and 3D points
 * 
 * @param cameras - Array of cameras from COLMAP reconstruction
 * @param points - Array of 3D points from COLMAP reconstruction
 * @param precomputedMapping - Optional pre-computed camera_to_points from JSON export
 * @returns PointCloudMappings with both directions for fast lookups
 * 
 * Performance: O(n) where n = total camera-point observations
 * Typical: 10,000 points × 10 cameras/point = 100,000 ops in ~10-20ms
 */
export function buildPointCloudMappings(
  cameras: Camera[],
  points: Point3D[],
  precomputedMapping?: Record<string, number[]>
): PointCloudMappings {
  const startTime = performance.now()
  
  // Initialize empty maps
  const cameraToPoints = new Map<number, Set<number>>()
  const pointToCameras = new Map<number, Set<number>>()
  
  // Pre-populate cameraToPoints with empty sets for all cameras
  // This ensures every camera has an entry, even if it observes no points
  for (const camera of cameras) {
    cameraToPoints.set(camera.id, new Set<number>())
  }
  
  // Use pre-computed mapping if available (faster, from Python export)
  if (precomputedMapping) {
    for (const [cameraIdStr, pointIds] of Object.entries(precomputedMapping)) {
      const cameraId = parseInt(cameraIdStr, 10)
      const pointSet = new Set(pointIds)
      cameraToPoints.set(cameraId, pointSet)
      
      // Build reverse mapping (point -> cameras)
      for (const pointId of pointIds) {
        if (!pointToCameras.has(pointId)) {
          pointToCameras.set(pointId, new Set())
        }
        pointToCameras.get(pointId)!.add(cameraId)
      }
    }
  } else {
    // Fallback: Build mappings from point cloud data
    for (const point of points) {
      const observingCameraIds = new Set<number>()

      if (!Array.isArray(point.cameras)) {
        pointToCameras.set(point.id, observingCameraIds)
        continue
      }

      for (const cameraRef of point.cameras) {
        const cameraId = cameraRef.camera_id
        observingCameraIds.add(cameraId)
        
        const cameraPoints = cameraToPoints.get(cameraId)
        if (cameraPoints) {
          cameraPoints.add(point.id)
        } else {
          cameraToPoints.set(cameraId, new Set([point.id]))
        }
      }
      
      pointToCameras.set(point.id, observingCameraIds)
    }
  }
  
  const elapsedMs = performance.now() - startTime
  console.log(`[PointCloud] Built mappings: ${cameras.length} cameras, ${points.length} points (${elapsedMs.toFixed(0)}ms)`)
  
  return { cameraToPoints, pointToCameras }
}

/**
 * Get all 3D points visible from a specific camera
 * 
 * @param cameraId - Camera ID to query
 * @param mappings - Point cloud mappings from buildPointCloudMappings()
 * @returns Set of point IDs visible from this camera, or empty Set if camera not found
 */
export function getCameraPoints(
  cameraId: number,
  mappings: PointCloudMappings
): Set<number> {
  return mappings.cameraToPoints.get(cameraId) || new Set()
}

/**
 * Get all cameras that observe a specific 3D point
 * 
 * @param pointId - Point ID to query
 * @param mappings - Point cloud mappings from buildPointCloudMappings()
 * @returns Set of camera IDs that observe this point, or empty Set if point not found
 */
export function getPointCameras(
  pointId: number,
  mappings: PointCloudMappings
): Set<number> {
  return mappings.pointToCameras.get(pointId) || new Set()
}

/**
 * Count shared 3D points between two cameras
 * Useful for navigation continuity - prefer cameras with more shared points
 * 
 * @param cameraId1 - First camera ID
 * @param cameraId2 - Second camera ID
 * @param mappings - Point cloud mappings
 * @returns Number of points visible from both cameras
 */
export function countSharedPoints(
  cameraId1: number,
  cameraId2: number,
  mappings: PointCloudMappings
): number {
  const points1 = getCameraPoints(cameraId1, mappings)
  const points2 = getCameraPoints(cameraId2, mappings)
  
  // Count intersection
  let sharedCount = 0
  for (const pointId of points1) {
    if (points2.has(pointId)) {
      sharedCount++
    }
  }
  
  return sharedCount
}

/**
 * Get cameras that observe a specific point, sorted by distance to that point
 * 
 * @param pointId - Point ID to query
 * @param point - The Point3D object with position
 * @param cameras - Array of all cameras
 * @param mappings - Point cloud mappings
 * @returns Array of cameras sorted by distance (closest first)
 */
export function getCamerasSortedByDistance(
  pointId: number,
  point: Point3D,
  cameras: Camera[],
  mappings: PointCloudMappings
): Camera[] {
  const observingCameraIds = getPointCameras(pointId, mappings)
  
  // Filter to cameras that observe this point
  const observingCameras = cameras.filter(cam => observingCameraIds.has(cam.id))
  
  // Sort by distance to point (closest first)
  return observingCameras.sort((a, b) => {
    const distA = distanceToPoint(a.position, point.position)
    const distB = distanceToPoint(b.position, point.position)
    return distA - distB
  })
}

/**
 * Calculate Euclidean distance between two 3D points
 */
function distanceToPoint(
  pos1: [number, number, number],
  pos2: [number, number, number]
): number {
  const dx = pos1[0] - pos2[0]
  const dy = pos1[1] - pos2[1]
  const dz = pos1[2] - pos2[2]
  return Math.sqrt(dx * dx + dy * dy + dz * dz)
}
